import { z } from "zod/v4";
import { NaturalezaReceptorEnum, TipoContribuyenteEnum, TipoDocumentoIdentidadEnum, TipoOperacionEnum } from "@/shared/types/contribuyente";

export const addClienteFormSchema = z.object({
  id: z.string().optional(),
  // Campos SIFEN
  naturaleza: z.number().int().default(NaturalezaReceptorEnum.Contribuyente),
  tipoOperacion: z.number().int().default(TipoOperacionEnum.B2C),

  // para NO contribuyentes (iTipIDRec)
  tipoDocumento: z.number().int().default(TipoDocumentoIdentidadEnum.CedulaParaguaya),

  // para contribuyentes
  tipoContribuyente: z.number().int().nullable().default(TipoContribuyenteEnum.PersonaFisica),

  // para contribuyentes (dRucRec, dDVRec) - SIFEN requiere entre 3 y 8 caracteres
  ruc: z.string().trim().nullable().optional().or(z.literal(""))
    .refine(v => !v || v === "" || (v.length >= 3 && v.length <= 8), {
      message: "El RUC debe tener entre 3 y 8 caracteres"
    }),
  dv: z.string().trim().nullable().optional().or(z.literal(""))
    .refine(v => !v || v === "" || v.length === 1, {
      message: "El DV debe tener 1 carácter"
    }),

  // para NO contribuyentes
  numeroDocumento: z.string().nullable().optional().or(z.literal("")),

  // Datos comunes
  razonSocial: z.string().min(4, "La Razón Social debe tener mínimo 4 caracteres").max(255, "La Razón Social debe tener máximo 255 caracteres"),
  nombreFantasia: z.string().nullable().optional().or(z.literal(""))
    .refine(v => !v || v === "" || (v.length >= 4 && v.length <= 255), {
      message: "El Nombre Fantasía debe tener entre 4 y 255 caracteres"
    }),
  direccion: z.string().min(1, "Dirección es requerida").max(255, "La dirección debe tener máximo 255 caracteres"),
  numeroCasa: z.number({ message: "Número es requerido" })
    .int("Número de casa debe ser un número entero")
    .min(0, "Número de casa debe ser mayor o igual a 0")
    .max(999999, "Número de casa debe ser menor a 999999"),
  telefono: z.string().nullable().optional().or(z.literal(""))
    .refine(v => !v || v === "" || (v.length >= 6 && v.length <= 15), {
      message: "El teléfono debe tener entre 6 y 15 caracteres"
    }),
  celular: z.string().nullable().optional().or(z.literal(""))
    .refine(v => !v || v === "" || (v.length >= 10 && v.length <= 20), {
      message: "El celular debe tener entre 10 y 20 caracteres"
    }),
  email: z.string().email("Email inválido").min(3, "El email debe tener mínimo 3 caracteres").max(80, "El email debe tener máximo 80 caracteres"),
  observaciones: z.string().nullable().optional().or(z.literal("")),

  // Ubicación - IDs
  paisId: z.string().nonempty("País es requerido"),
  departamentoId: z.string().nonempty("Departamento es requerido"),
  ciudadId: z.string().nonempty("Ciudad es requerida"),

  // Ubicación - Nombres para display
  departamentoSeleccionado: z.string().optional(),
  ciudadSeleccionada: z.string().optional(),

  // Campos DE
  codigoPais: z.string().default("PRY"),
}).superRefine((value, ctx) => {

  // Validación para tipo de operación B2B: debe ser Persona Jurídica
  if (value.tipoOperacion === TipoOperacionEnum.B2B) {
    if (value.tipoContribuyente !== TipoContribuyenteEnum.PersonaJuridica) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["tipoContribuyente"],
        message: "Para operaciones B2B, el tipo de contribuyente debe ser Persona Jurídica"
      });
    }
  }

  if (value.naturaleza === NaturalezaReceptorEnum.Contribuyente) {
    const ruc = value.ruc?.trim();
    if (!ruc) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ruc"],
        message: "El RUC es requerido para contribuyentes"
      });
    }

    const dv = value.dv?.trim();
    if (!dv) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dv"],
        message: "El DV es requerido para contribuyentes"
      });
    }

    // Validación: Contribuyentes solo pueden tener tipo de operación B2B, B2C o B2G
    if (
      value.tipoOperacion !== TipoOperacionEnum.B2B &&
      value.tipoOperacion !== TipoOperacionEnum.B2C &&
      value.tipoOperacion !== TipoOperacionEnum.B2G
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["tipoOperacion"],
        message: "Los contribuyentes solo pueden tener tipo de operación B2B, B2C o B2G"
      });
    }

    // Validación para tipo de operación B2G: debe ser Persona Jurídica
    if (value.tipoOperacion === TipoOperacionEnum.B2G) {
      if (value.tipoContribuyente !== TipoContribuyenteEnum.PersonaJuridica) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["tipoContribuyente"],
          message: "Para operaciones B2G (Gobierno), el tipo de contribuyente debe ser Persona Jurídica"
        });
      }
    }
  }
  else if (value.naturaleza === NaturalezaReceptorEnum.NoContribuyente) {
    const numeroDocumento = value.numeroDocumento?.trim();
    if (!numeroDocumento) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["numeroDocumento"],
        message: "El Número de Documento es requerido para no contribuyentes"
      });
    }
    else if (value.tipoDocumento !== TipoDocumentoIdentidadEnum.Innominado && (numeroDocumento?.length < 3 || numeroDocumento?.length > 8)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["numeroDocumento"],
        message: "El Número de Documento debe tener entre 3 y 8 caracteres"
      });
    }
  }

  // Validación para tipo de operación B2F (Extranjero)
  if (value.tipoOperacion === TipoOperacionEnum.B2F) {
    if (value.codigoPais === "PRY") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["paisId"],
        message: "Para operaciones B2F (Extranjero), el país debe ser diferente a Paraguay"
      });
    }
  }
});
